import React from 'react'
import './iso-box.css'

/**
 * feat-004 story 2.4 (UX-DR3) — the single source of isometric-box rendering
 * for a display option. Lives under `menus/` (not `card/`) per architecture's
 * declared structure: it is SHARED between this story's card and story 4.4's
 * not-yet-built flyout, so it needs a neutral home rather than living inside
 * either consumer's own directory.
 *
 * Renders an isometric box with the product image on the front face. Sizes:
 * S = 24px (flyout rows), M = 150px (card hero preview) — UX spec §Component
 * Strategy.
 */

export type IsoBoxSize = 'S' | 'M'

export interface IsoBoxProps {
  /** The image to show on the box's front face — the BASE product image for
   * this story (no per-option dedicated image resolution until story 2.6). */
  imageUrl: string
  /** 1-6 for orientation rows, null for display-mode rows. */
  orientationKey: number | null
  /**
   * The option's own effective W/H/D, so the box is drawn at that option's
   * real PROPORTIONS (reported by QA 2026-10-07).
   *
   * Source ticket T-10 specified "קופסה איזומטרית ב-SVG הנגזרת
   * מהמידות" — an isometric box DERIVED FROM THE DIMENSIONS — and
   * PLAN-TC-1377 checks exactly that ("Isometric box proportions correspond
   * to Side dimensions", and a display mode's preview "updates to
   * display-mode proportions"). The shipped box was a hardcoded cube, so
   * all six orientations and every display mode drew identically and
   * PLAN-TC-1376/1377 could not pass.
   *
   * Optional: omitted, the geometry falls back to the cube this component
   * has always drawn, so any caller that has no dims in hand is unchanged.
   */
  dims?: { width: number; height: number; depth: number } | null
  /** True once story 2.6 wires a real per-option dedicated image in. */
  isDedicated: boolean
  size: IsoBoxSize
  /** Translated orientation/mode name — used as the accessible label. */
  label: string
}

/**
 * IsoBox dimming rule (inferred — see story Dev Notes "IsoBox dimming rule"):
 * neither DisplayOptionDto (this story) nor DisplayOptionReadDto (story 2.2)
 * exposes a server-provided `isDimmed` field, so this is computed here from
 * already-known fields, mirroring the backend's `effectiveImage()` fallback
 * exactly (Planigo/src/support/display-options/effective-resolution.ts):
 * Front (1) and Front Laydown (2) are never dimmed even without a dedicated
 * image; every other orientation (3-6) and every display mode ARE dimmed
 * unless a dedicated image exists. This is a deterministic rule from
 * `orientationKey`, not "derived by comparing URLs" (the actual architecture
 * anti-pattern this must not violate).
 */
export function computeIsDimmed(
  orientationKey: number | null,
  isDedicated: boolean
): boolean {
  if (isDedicated) {
    return false
  }
  return !(orientationKey === 1 || orientationKey === 2)
}

/**
 * The three projected extents of the box, in the 100x100 viewBox.
 *
 * Isometric projection: the WIDTH axis runs right-and-down, the DEPTH axis
 * left-and-down, the HEIGHT axis straight down. The constants are read off
 * the cube this component used to hardcode, so `w === h === d` reproduces
 * that cube EXACTLY (same 12 points) and nothing changes for a cubic
 * option; only a non-cubic one now looks like itself.
 *
 * The longest of the three axes gets the full extent and the other two
 * shrink in proportion, which keeps every box inside the viewBox at any
 * aspect ratio while preserving the ratios between the axes.
 */
/*
 * Oblique ("cabinet") projection, matching the SOW figure (reported
 * 2026-10-09). Its boxes draw the front face as an UPRIGHT RECTANGLE with
 * the depth running up and to the right; measuring all six confirmed the
 * offset is at 45 degrees, dx == dy to within a pixel on every one of them.
 * The projection this replaces slanted BOTH vertical faces, which is what
 * made the icons read as tilted blocks beside the figure's front-facing
 * boxes.
 *
 * KD is the one value the figure could not simply be copied on. It draws a
 * FIXED ~10px offset on every box regardless of depth, because it is a
 * schematic: its six front faces are indicative sizes, not the permutations
 * of one product's dims (checked - they do not follow the orientation
 * table). A fixed offset here would throw away the depth proportionality
 * FR-10 requires and PLAN-TC-1376/1377 pin, so the offset stays
 * proportional. The constant is chosen so realistic dims land on the
 * figure's own look: for a 10x16x6 product the offset comes out at 0.19 of
 * the front height, against the figure's measured 0.16.
 */
const KW = 56 // horizontal extent of the longest in-plane axis
const KH = 56 // vertical extent of the height axis
const KD = 28 // depth offset, up and right at 45 degrees

/**
 * Floor under each axis's share of its full extent (reported 2026-10-08).
 *
 * Straight proportionality collapses a face whenever one dimension is tiny
 * next to the others — a flat snack bag (wide and tall, a centimetre or two
 * deep) drew a depth axis under half a pixel, so the box lost its side
 * entirely and read as a flat RECTANGLE rather than a 3D box. This is a
 * 24px row icon and a thumbnail: it has to say "box" first and "these
 * proportions" second.
 *
 * Clamping the RATIO (not the final size) keeps the comparison honest — a
 * narrow option still draws visibly narrower than a wide one, because
 * anything above the floor scales normally — while guaranteeing all three
 * faces stay visible. At 0.3 the shortest axis is never less than ~30% of
 * the longest, which is enough to read as a solid at 24px.
 *
 * A cube is unaffected (every ratio is already 1), so the hardcoded-cube
 * geometry this module shipped with is still reproduced exactly.
 */
const MIN_AXIS_RATIO = 0.3

/**
 * Face shading, taken from the SOW's own figure (reported 2026-10-09: "i
 * need those like exactly same as the sow, colors and shadows and
 * everything"). The figure is not in this repo as a vector source, so the
 * values were SAMPLED out of it rather than matched by eye: a vertical scan
 * down a box's centre crosses background, top face, front face; a horizontal
 * scan across its middle crosses front face then side face. The runs of flat
 * colour along those two lines name every face unambiguously.
 *
 *   front  #FFFFFF    top  #E6E1F4    side  #D8D0EC    stroke  #633FA6
 *
 * The stroke is the darkest value found on the outlines and appears as a
 * flat run, not only as blend; antialiasing can lighten a 1px line toward
 * its neighbour but never darken it, so this is the colour itself. Note it
 * is NOT the brand `#5128A0` — the figure draws these a shade lighter.
 *
 * **A white front face was previously a bug, and is safe now.** The fills
 * this replaces were chosen on 2026-10-08 because the original side face was
 * `#FFFFFF`, identical to `.simv3-doc__list` behind it, so one face vanished
 * into the surface. The SOW puts white on the FRONT, the largest face, which
 * would be the same trap — except the other half of that fix is what makes
 * it work: at 24px this 100-unit viewBox renders at 0.24 device px per unit,
 * so `strokeWidth=1` used to smear away to nothing and the fills alone had to
 * carry the form. `vector-effect: non-scaling-stroke` now pins every outline
 * to 1 DEVICE pixel at any size, so the form is carried by the purple
 * outline exactly as it is in the figure, and the fills no longer have to
 * separate themselves from the background on their own.
 */
const FACE_FRONT = '#FFFFFF'
const FACE_TOP = '#E6E1F4'
const FACE_SIDE = '#D8D0EC'
const FACE_STROKE = '#633FA6'

export const isoGeometry = (
  dims?: { width: number; height: number; depth: number } | null
): {
  top: string
  widthFace: string
  depthFace: string
  imageRect: { x: number; y: number; width: number; height: number }
} => {
  const w = dims && dims.width > 0 ? dims.width : 1
  const h = dims && dims.height > 0 ? dims.height : 1
  const d = dims && dims.depth > 0 ? dims.depth : 1
  const longest = Math.max(w, h, d)
  const share = (value: number) => Math.max(value / longest, MIN_AXIS_RATIO)
  const wShare = share(w)
  const hShare = share(h)
  const dShare = share(d)
  const ax = KW * wShare // front face width
  const hh = KH * hShare // front face height
  const dd = KD * dShare // depth offset, applied equally in x and -y
  // Centred both ways: the shape spans (ax + dd) horizontally and
  // (hh + dd) vertically.
  const x0 = (100 - (ax + dd)) / 2
  const y0 = (100 - (hh + dd)) / 2
  const fy = y0 + dd // the front face's top edge, dd below the back edge
  const round = (value: number) => Math.round(value * 100) / 100
  const point = (x: number, y: number) => `${round(x)},${round(y)}`
  const polygon = (...points: [number, number][]) =>
    points.map(([x, y]) => point(x, y)).join(' ')
  return {
    // Top face: front-top-left, back-top-left, back-top-right, front-top-right.
    top: polygon([x0, fy], [x0 + dd, y0], [x0 + ax + dd, y0], [x0 + ax, fy]),
    // The WIDTH-facing face — the product's front, and where an image goes.
    // An upright rectangle, which is the whole point of this projection.
    widthFace: polygon(
      [x0, fy],
      [x0 + ax, fy],
      [x0 + ax, fy + hh],
      [x0, fy + hh]
    ),
    // The DEPTH-facing face, down the right-hand side.
    depthFace: polygon(
      [x0 + ax, fy],
      [x0 + ax + dd, y0],
      [x0 + ax + dd, y0 + hh],
      [x0 + ax, fy + hh]
    ),
    // The width face IS a rectangle now, so this is simply its box
    // (TC-1513's "stretched to fit the cell").
    imageRect: {
      x: round(x0),
      y: round(fy),
      width: round(ax),
      height: round(hh),
    },
  }
}

export const IsoBox: React.FC<IsoBoxProps> = ({
  imageUrl,
  orientationKey,
  isDedicated,
  size,
  label,
  dims,
}) => {
  const isDimmed = computeIsDimmed(orientationKey, isDedicated)
  const geometry = isoGeometry(dims)
  const className = [
    'simv3-isobox',
    size === 'S' ? 'simv3-isobox--s' : 'simv3-isobox--m',
    isDimmed ? 'simv3-isobox--dimmed' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <svg
      className={className}
      viewBox="0 0 100 100"
      role="img"
      aria-label={label}
      data-testid="iso-box"
      data-dimmed={isDimmed}
      data-size={size}
      data-proportioned={dims ? 'true' : 'false'}
    >
      {/* Minimal isometric box: a simple 3-face parallelogram outline is
          sufficient here — the visual reference implementation is the
          round-5 prototype (docs/display-options/assets/T-22-ui-prototype.html).
          The three faces are now PROPORTIONED from the option's own
          dimensions (see `isoGeometry`); with no dims, or a cubic option,
          these are the exact same 12 points the hardcoded cube drew. */}
      <polygon
        points={geometry.top}
        fill={FACE_TOP}
        stroke={FACE_STROKE}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={geometry.widthFace}
        fill={FACE_FRONT}
        stroke={FACE_STROKE}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={geometry.depthFace}
        fill={FACE_SIDE}
        stroke={FACE_STROKE}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      {imageUrl ? (
        <image
          className="simv3-isobox__image"
          href={imageUrl}
          x={geometry.imageRect.x}
          y={geometry.imageRect.y}
          width={geometry.imageRect.width}
          height={geometry.imageRect.height}
          preserveAspectRatio="none"
        />
      ) : null}
    </svg>
  )
}
